/**
 * Derivações da tela "Envios ao Legado" (spec 039).
 *
 * Tudo que a tela calcula vive aqui, fora do `render` — lição das specs 035/036: lógica dentro de
 * `Table.Column` não é testável, e um mock de coluna no-app esconde o critério de aceite da célula.
 */

import { ILegacySendItem, ILegacyConsumerLog } from "interfaces";
import { beneficiaryLabel, errorKindMessage } from "enums";
import { formatDuration, formatSeconds } from "utils/duration";

/** `formatDuration` recebe início/fim; aqui só há a duração em segundos já calculada. */
const formatElapsedLabel = (seconds: number): string => {
  const end = new Date();
  const start = new Date(end.getTime() - seconds * 1000);
  return formatDuration(start.toISOString(), end.toISOString());
};

/** "8 de 9" — itens aceitos sobre o total enviado. Sem contador, exibe "—". */
export const acceptedRatio = (log?: ILegacyConsumerLog | null): string => {
  const counters = log?.counters;
  if (!counters || typeof counters.total !== "number") {
    return "—";
  }
  return `${counters.sent ?? 0} de ${counters.total}`;
};

/** Número formatado em pt-BR, ou "—" quando ausente (campo ausente ≠ zero — lição da 033). */
export const metricNumber = (value?: number | null): string => {
  if (typeof value !== "number") {
    return "—";
  }
  return value.toLocaleString("pt-BR");
};

/** Percentual em pt-BR. `null` é ausência de base de cálculo, não 0%. */
export const metricPercent = (value?: number | null): string => {
  if (typeof value !== "number") {
    return "—";
  }
  return `${value.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

/**
 * Linha da tabela de itens do detalhe: um item por POST feito ao legado.
 *
 * O índice do dependente é derivado da **ordem** dos itens (o backend grava na ordem do envio):
 * cada `target: "dependent"` incrementa o contador, e os anexos que vêm depois pertencem a ele.
 */
export interface LegacySendItemRow {
  key: string;
  beneficiary: string;
  document: string;
  outcome?: string | null;
  httpStatus?: number | null;
  reason: string;
}

export const buildItemRows = (
  items?: ILegacySendItem[] | null,
): LegacySendItemRow[] => {
  if (!items || items.length === 0) {
    return [];
  }

  const rows: LegacySendItemRow[] = [];
  let dependentIndex = -1;
  let currentTarget: string | null = null;

  items.forEach((item, position) => {
    if (item.target === "dependent") {
      dependentIndex += 1;
      currentTarget = "dependent";
    } else if (item.target === "holder") {
      currentTarget = "holder";
    }

    const owner =
      item.target === "attachment"
        ? beneficiaryLabel(currentTarget ?? "holder", dependentIndex)
        : beneficiaryLabel(item.target, dependentIndex);

    rows.push({
      key: `${position}-${item.target ?? "item"}`,
      beneficiary: owner,
      document: documentLabel(item),
      outcome: item.outcome,
      httpStatus: item.http_status,
      reason: itemReason(item),
    });
  });

  return rows;
};

/** "Identidade (frente)" — tipo do documento + lado. Cadastro (não-anexo) não tem documento. */
export const documentLabel = (item?: ILegacySendItem | null): string => {
  if (!item || item.target !== "attachment") {
    return "—";
  }
  const type = item.document_type ? `Documento ${item.document_type}` : "Anexo";
  return item.side ? `${type} (${item.side})` : type;
};

/** Motivo da falha em pt-BR; item aceito não tem motivo. */
export const itemReason = (item?: ILegacySendItem | null): string => {
  if (!item || item.outcome === "sent") {
    return "—";
  }
  return errorKindMessage(item.error_kind) ?? "—";
};

/**
 * Duração legível. Reusa `utils/duration.ts` (vocabulário já estabelecido pela tela de Recarga):
 * `4,2 s` abaixo de um minuto, `4 min 12 s` acima — nada de inventar um segundo formato de duração
 * no produto.
 */
export const formatDurationSeconds = (value?: number | null): string => {
  if (typeof value !== "number") {
    return "—";
  }
  if (value < 60) {
    return formatSeconds(value);
  }
  return formatElapsedLabel(value);
};

/**
 * A tela precisa saber se há falha para acentuar o card — e **campo ausente não é zero** (lição da
 * 033): sem o contador, não se afirma que houve zero falhas.
 */
export const hasFailures = (value?: number | null): boolean =>
  typeof value === "number" && value > 0;

/**
 * Parâmetros do `/summary` derivados dos filtros aplicados (R01 do review).
 *
 * Sem isso a faixa de resumo mostra o total de **toda** a retenção enquanto a caption promete "no
 * período filtrado" — o operador compara um número global com uma tabela filtrada. Fica aqui, e não
 * no componente, para ser testável (lição das specs 035/036).
 *
 * `kind` é filtro permanente da aba e não entra: o `/summary` já separa envios de execuções.
 */
export const summaryParamsFromFilters = (
  filters?: Array<{ field?: string; value?: unknown }> | null,
): Record<string, string> => {
  const params: Record<string, string> = {};
  const allowed = ["control_number", "start_date", "end_date"];

  (filters ?? []).forEach((filter) => {
    const field = filter?.field;
    const value = filter?.value;
    if (!field || !allowed.includes(field)) {
      return;
    }
    if (value === undefined || value === null || value === "") {
      return;
    }
    params[field] = String(value);
  });

  return params;
};
